import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { EditIcon, DeleteIcon } from "../../../Icons";
import Table from "../../../components/UI/Table";
import { CELL_MUTED, CELL_STRONG } from "../../../ui/pageChrome";
import {
  INVOICE_THEME,
  INVOICE_BTN,
  INVOICE_COL,
  INVOICE_ROLE_CHIP,
} from "../invoiceTheme";
import InvoiceMarksRow from "./InvoiceMarksRow";
import { isInvoiceEditLocked } from "../invoiceMarks";
import { computeInvoiceTotal } from "../invoiceLineItem";

const InvoiceHistory = ({
  invoices,
  onRequestDelete,
  onRequestEdit,
  onRemoveMark,
  formatDate,
  formatCurrency,
  canDelete = false,
  /** Admin/OA — edit stays enabled even after Reviewed/Paid. */
  canEditWhenFlagged = false,
  canManageFlags = false,
  canSelect = false,
  selectedIds,
  onToggleRow,
  onToggleAll,
  loading = false,
  pagination,
}) => {
  const [removingKey, setRemovingKey] = React.useState(null);
  const [sortKey, setSortKey] = React.useState("createdAt");
  const [sortOrder, setSortOrder] = React.useState("desc");

  const handleSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortOrder("asc");
      }
    },
    [sortKey],
  );

  const sortedInvoices = useMemo(() => {
    const rows = [...(invoices || [])];
    if (!sortKey) return rows;

    const getVal = (row) => {
      if (sortKey === "userName") {
        return `${row.employer?.firstName || ""} ${row.employer?.lastName || ""}`.trim();
      }
      if (sortKey === "invoiceData") {
        return row.invoiceData?.length || 0;
      }
      if (sortKey === "totalAmount") {
        return computeInvoiceTotal(row.invoiceData);
      }
      if (sortKey === "marks") {
        return (row.marks || []).length;
      }
      return row[sortKey];
    };

    rows.sort((a, b) => {
      let aVal = getVal(a);
      let bVal = getVal(b);
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sortOrder === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [invoices, sortKey, sortOrder]);

  const pageIds = useMemo(
    () => sortedInvoices.map((r) => r.id).filter((id) => id != null),
    [sortedInvoices],
  );
  const allSelected =
    canSelect && pageIds.length > 0 && pageIds.every((id) => selectedIds?.has(id));
  const someSelected =
    canSelect && pageIds.some((id) => selectedIds?.has(id)) && !allSelected;

  const columns = useMemo(
    () => [
      {
        header: "Invoice #",
        accessor: "invoiceNumber",
        sortable: true,
        className: INVOICE_COL.number,
        render: (row) => (
          <span className={`${CELL_STRONG} text-indigo-700 tabular-nums`}>
            #{row.invoiceNumber}
          </span>
        ),
      },
      {
        header: "Date Created",
        accessor: "createdAt",
        sortable: true,
        className: INVOICE_COL.created,
        render: (row) => (
          <span className={`${CELL_MUTED} tabular-nums`}>
            {formatDate(row.createdAt) || "—"}
          </span>
        ),
      },
      {
        header: "Employee",
        accessor: "userName",
        sortable: true,
        className: INVOICE_COL.employee,
        render: (row) => (
          <span className={`${CELL_STRONG} truncate block`}>
            {[row.employer?.firstName, row.employer?.lastName]
              .filter(Boolean)
              .join(" ") || "—"}
          </span>
        ),
      },
      {
        header: "Role",
        accessor: "role",
        sortable: true,
        className: INVOICE_COL.role,
        render: (row) => (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${INVOICE_ROLE_CHIP}`}
          >
            {row.role || "—"}
          </span>
        ),
      },
      {
        header: "Period",
        accessor: "formDate",
        sortable: true,
        className: INVOICE_COL.period,
        render: (row) => (
          <span className={`${CELL_MUTED} tabular-nums`}>
            {formatDate(row.formDate)} – {formatDate(row.toDate)}
          </span>
        ),
      },
      {
        header: "Due Date",
        accessor: "dueDate",
        sortable: true,
        className: INVOICE_COL.due,
        render: (row) => {
          const due = formatDate(row.dueDate);
          return (
            <span
              className={`${due ? CELL_STRONG : CELL_MUTED} tabular-nums whitespace-nowrap`}
            >
              {due || "—"}
            </span>
          );
        },
      },
      {
        header: "Notes",
        accessor: "notes",
        sortable: true,
        className: INVOICE_COL.notes,
        render: (row) => (
          <span
            className={`${CELL_MUTED} block truncate`}
            title={row.notes || ""}
          >
            {row.notes?.trim() || "—"}
          </span>
        ),
      },
      {
        header: "Flags",
        accessor: "marks",
        sortable: true,
        className: INVOICE_COL.flags,
        render: (row) => (
          <InvoiceMarksRow
            marks={row.marks || []}
            size="sm"
            onRemoveMark={
              canManageFlags
                ? async (markType) => {
                    const key = `${row.id}:${markType}`;
                    setRemovingKey(key);
                    try {
                      await onRemoveMark?.(row, markType);
                    } finally {
                      setRemovingKey(null);
                    }
                  }
                : undefined
            }
            removingMarkType={
              removingKey?.startsWith(`${row.id}:`)
                ? removingKey.split(":")[1]
                : null
            }
          />
        ),
      },
      {
        header: "Total Amount",
        accessor: "totalAmount",
        sortable: true,
        className: INVOICE_COL.amount,
        render: (row) => (
          <span className={`${CELL_STRONG} tabular-nums`}>
            {formatCurrency(computeInvoiceTotal(row.invoiceData))}
          </span>
        ),
      },
      {
        header: "Actions",
        id: "actions",
        className: INVOICE_COL.actions,
        render: (row) => {
          const locked =
            !canEditWhenFlagged && isInvoiceEditLocked(row);
          return (
            <div className="inline-flex items-center justify-end gap-1 flex-nowrap">
              <button
                type="button"
                onClick={() => {
                  if (locked) return;
                  onRequestEdit?.(row);
                }}
                disabled={locked}
                className={INVOICE_BTN.icon}
                title={
                  locked
                    ? "Cannot edit after Reviewed or Paid"
                    : "Edit invoice"
                }
                aria-label={
                  locked
                    ? "Cannot edit after Reviewed or Paid"
                    : "Edit invoice"
                }
              >
                <EditIcon size={14} />
              </button>
              {canDelete ? (
                <button
                  type="button"
                  onClick={() => onRequestDelete(row)}
                  className={INVOICE_BTN.iconDanger}
                  title="Delete invoice"
                  aria-label="Delete invoice"
                >
                  <DeleteIcon size={14} />
                </button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [
      canDelete,
      canEditWhenFlagged,
      canManageFlags,
      onRequestDelete,
      onRequestEdit,
      onRemoveMark,
      formatDate,
      formatCurrency,
      removingKey,
    ],
  );

  return (
    <Table
      columns={columns}
      data={sortedInvoices}
      keyField="id"
      loading={loading}
      showSrNo
      density="compact"
      embedded
      tableClassName="table-fixed"
      headTone={INVOICE_THEME.tableHead}
      sortKey={sortKey}
      sortOrder={sortOrder}
      onSort={handleSort}
      noDataMessage="No invoices found."
      pagination={pagination}
      selection={
        canSelect
          ? {
              enabled: true,
              isSelected: (row) => selectedIds?.has(row.id),
              onToggleRow: (row, _index, checked) =>
                onToggleRow?.(row.id, checked),
              onToggleAll: (checked) => onToggleAll?.(pageIds, checked),
              allSelected,
              someSelected,
            }
          : undefined
      }
      combineSelectSrNo
    />
  );
};

InvoiceHistory.propTypes = {
  invoices: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRequestDelete: PropTypes.func.isRequired,
  onRequestEdit: PropTypes.func,
  formatDate: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
  canEditWhenFlagged: PropTypes.bool,
  canManageFlags: PropTypes.bool,
  canSelect: PropTypes.bool,
  selectedIds: PropTypes.instanceOf(Set),
  onToggleRow: PropTypes.func,
  onToggleAll: PropTypes.func,
  onRemoveMark: PropTypes.func,
  loading: PropTypes.bool,
  pagination: PropTypes.object,
};

export default InvoiceHistory;
